<template>
  <div class="list-car-fee-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('listCarFee.page.title', { carNum: listCarFeeInfo.carNum })"
      :subtitle="$t('listCarFee.page.subtitle', { parkingName: listCarFeeInfo.parkingName })">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="_openCarCreateFeeAddModal">
          {{ $t('listCarFee.createFee') }}
        </el-button>
        <el-button icon="el-icon-close" @click="_goBack">
          {{ $t('common.back') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="_loadlistCarFeeInfo(page.current, page.size)">
        <template #left>
          <span class="ds-panel__title">{{ $t('listCarFee.fee') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="listCarFeeInfo.fees" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('listCarFee.page.empty')" @retry="_loadlistCarFeeInfo(1, 10)">
        <el-table-column prop="feeName" :label="$t('listCarFee.feeItem')" align="left" min-width="140"
          show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="ds-link" @click="_viewCarFeeConfig(scope.row)">
              {{ scope.row.feeName }}
              <i class="el-icon-info"></i>
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="feeFlagName" :label="$t('listCarFee.feeFlag')" align="center" width="130">
          <template slot-scope="scope">
            {{ scope.row.feeFlagName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="feeTypeCdName" :label="$t('listCarFee.feeType')" align="center" width="130">
          <template slot-scope="scope">
            {{ scope.row.feeTypeCdName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="amountOwed" :label="$t('listCarFee.amountOwed')" align="right" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.amountOwed) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="startTime" :label="$t('listCarFee.startTime')" align="center" width="170">
          <template slot-scope="scope">
            {{ formatDateTimeSec(scope.row.startTime) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('listCarFee.endTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ _getEndTime(scope.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('listCarFee.deadlineTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ _getDeadlineTime(scope.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('listCarFee.remark')" align="left" min-width="190">
          <template slot-scope="scope">
            <div v-if="scope.row.feeTypeCd == '888800010015' || scope.row.feeTypeCd == '888800010016'">
              <div>
                <span>{{ $t('listCarFee.preDegrees') }}：</span><span class="ds-num">{{ scope.row.preDegrees }}</span>
              </div>
              <div>
                <span>{{ $t('listCarFee.curDegrees') }}：</span><span class="ds-num">{{ scope.row.curDegrees }}</span>
              </div>
              <div>
                <span>{{ $t('listCarFee.price') }}：</span><span class="ds-num">{{ scope.row.mwPrice ? scope.row.mwPrice :
                  scope.row.squarePrice }}</span>
              </div>
              <div>
                <span>{{ $t('listCarFee.additionalAmount') }}：</span><span class="ds-num">{{ scope.row.additionalAmount }}</span>
              </div>
            </div>
            <div v-else-if="scope.row.feeTypeCd == '888800010017'">
              <div>
                <span>{{ $t('listCarFee.algorithm') }}：</span>{{ _getAttrValue(scope.row.feeAttrs, '390005') }}
              </div>
              <div>
                <span>{{ $t('listCarFee.usage') }}：</span>{{ _getAttrValue(scope.row.feeAttrs, '390003') }}
              </div>
            </div>
            <div v-else>
              <div>
                <span>{{ $t('listCarFee.price') }}：</span><span class="ds-num">{{ scope.row.squarePrice }}</span>
              </div>
              <div>
                <span>{{ $t('listCarFee.additionalAmount') }}：</span><span class="ds-num">{{ scope.row.additionalAmount }}</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('listCarFee.state')" align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.stateName || '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('listCarFee.operation')" align="center" min-width="220">
          <template slot-scope="scope">
            <el-button type="text" v-if="scope.row.state != '2009001' && hasPrivilege('502020082314267912')"
              @click="_payFee(scope.row)">{{ $t('listCarFee.pay') }}</el-button>
            <el-button type="text" @click="_payFeeHis(scope.row)">{{ $t('listCarFee.history') }}</el-button>
            <el-button type="text" class="ds-btn-danger" v-if="hasPrivilege('502020090604200029')"
              @click="_deleteFee(scope.row)">{{ $t('listCarFee.cancel') }}</el-button>
            <el-button type="text" v-if="scope.row.state != '2009001' && hasPrivilege('502020090427190001')"
              @click="_editFee(scope.row)">{{ $t('listCarFee.change') }}</el-button>
            <el-button type="text" v-if="scope.row.feeFlag == '4012024' && scope.row.state == '2008001'"
              @click="_splitPayFee(scope.row)">{{ $t('listCarFee.split') }}</el-button>
            <el-button type="text" @click="_viewCarFee(scope.row)">{{ $t('listCarFee.detail') }}</el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <div class="ds-tip-text">
            <div>{{ $t('listCarFee.note1') }}</div>
            <div>{{ $t('listCarFee.note2') }}</div>
          </div>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 子组件 -->
    <delete-fee ref="deleteFee" @success="handleSuccess"></delete-fee>
    <edit-fee ref="editFee" @success="handleSuccess"></edit-fee>
    <split-fee ref="splitFee" @success="handleSuccess"></split-fee>
    <car-create-fee-add ref="carCreateFeeAdd" @success="handleSuccess"></car-create-fee-add>
    <!-- <add-meter-water ref="addMeterWater"></add-meter-water> -->
  </div>
</template>

<script>
import { listFee } from '@/api/fee/listCarFeeApi'
import DeleteFee from '@/components/fee/deleteFee'
import EditFee from '@/components/fee/editFee'
import SplitFee from '@/components/fee/splitFee'
import CarCreateFeeAdd from '@/components/fee/carCreateFeeAdd'
//import AddMeterWater from '@/components/fee/addMeterWater'
import { getCommunityId } from '@/api/community/communityApi'
import { dateFormat } from '@/utils/dateUtil'
import { formatDateTimeSec, formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ListCarFee',
  components: {
    DeleteFee,
    EditFee,
    SplitFee,
    CarCreateFeeAdd,
    // AddMeterWater
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      listCarFeeInfo: {
        fees: [],
        carNum: '',
        carId: '',
        total: 0,
        records: 1,
        areaNum: '',
        num: '',
        parkingName: ''
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      communityId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    if (this.$route.query.carNum) {
      this.listCarFeeInfo.carNum = this.$route.query.carNum
      this.listCarFeeInfo.carId = this.$route.query.carId
      this.listCarFeeInfo.areaNum = this.$route.query.areaNum
      this.listCarFeeInfo.num = this.$route.query.num
    }
    let parkingName = this.listCarFeeInfo.areaNum ? `${this.listCarFeeInfo.areaNum}停车场${this.listCarFeeInfo.num}车位` : '无车位'
    this.listCarFeeInfo.parkingName = parkingName
    this._loadlistCarFeeInfo(1, 10)
  },
  methods: {
    handleSuccess() {
      this._loadlistCarFeeInfo(1, 10)
    },
    async _loadlistCarFeeInfo(page, row) {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: page,
          row: row,
          communityId: this.communityId,
          payerObjId: this.listCarFeeInfo.carId
        }
        const { fees, total, records } = await listFee(params)
        this.listCarFeeInfo.fees = Array.isArray(fees) ? fees : []
        this.page.total = total || 0
        this.page.records = records
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('listCarFee.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    handleSizeChange(val) {
      this.page.size = val
      this._loadlistCarFeeInfo(this.page.current, val)
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._loadlistCarFeeInfo(val, this.page.size)
    },
    _payFee(fee) {
      fee.roomName = this.listCarFeeInfo.carNum
      this.$router.push({ path: '/views/fee/payFeeOrder', query: { feeId: fee.feeId } })
    },
    _payFeeHis(fee) {
      this.$router.push({ path: '/views/fee/propertyFee', query: fee })
    },
    _editFee(fee) {
      this.$refs.editFee.open(fee)
    },
    _deleteFee(fee) {
      this.$refs.deleteFee.open({
        communityId: this.communityId,
        feeId: fee.feeId
      })
    },
    _openCarCreateFeeAddModal() {
      this.$refs.carCreateFeeAdd.open({
        isMore: false,
        car: this.listCarFeeInfo
      })
    },
    _openAddMeterWaterModal() {
      this.$refs.addMeterWater.open({
        roomId: this.listCarFeeInfo.carId,
        roomName: this.listCarFeeInfo.carNum,
        ownerName: this.listCarFeeInfo.parkingName,
        objType: '6666'
      })
    },
    _goBack() {
      this.$router.go(-1)
    },
    _getDeadlineTime(fee) {
      if (fee.amountOwed == 0 && fee.endTime == fee.deadlineTime) {
        return "-"
      }
      if (fee.state == '2009001') {
        return "-"
      }
      return dateFormat(fee.deadlineTime) || fee.deadlineTime
    },
    _getEndTime(fee) {
      if (fee.state == '2009001') {
        return "-"
      }
      return dateFormat(fee.endTime) || fee.endTime
    },
    _viewCarFeeConfig(fee) {
      // 查看费用项配置逻辑
      console.log(fee)
    },
    _viewCarFee(fee) {
      // 查看费用详情逻辑
      window.open(`/#/views/fee/feeDetail?feeId=${fee.feeId}`)
    },
    _splitPayFee(fee) {
      this.$refs.splitFee.open(fee)
    },
    _getAttrValue(attrs, specCd) {
      const attr = attrs.find(item => item.specCd === specCd)
      return attr ? attr.value : ''
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },
    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.list-car-fee-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 可点击链接样式 */
.ds-link {
  color: var(--ds-primary);
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}
</style>
